import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { 
  ChevronDown, 
  ChevronUp, 
  Zap, 
  Copy, 
  Check, 
  CheckCircle,
  Edit2,
  Calendar,
  Trash2,
  MoreVertical,
  ExternalLink,
  Facebook,
  Instagram,
  FileText,
  Image,
  Video,
  RefreshCw,
  Loader2,
  Mic,
  Upload,
  Volume2,
  Mail,
  Send,
  X,
  ThumbsUp,
  ThumbsDown,
  Download,
  Archive
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiClient, useAuth } from "@/lib/api";

const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: () => <span className="font-bold text-xs">TT</span>
};

const platformColors = {
  facebook: "#1877F2",
  instagram: "#E4405F",
  tiktok: "#000000"
};

// Content type icons
const getContentTypeIcon = (post) => {
  if (post.type === "video" || post.video_url) {
    return { icon: Video, color: "text-purple-400", label: "Video" };
  } else if (post.image_url) {
    return { icon: Image, color: "text-blue-400", label: "Image" };
  }
  return { icon: FileText, color: "text-gray-400", label: "Text" };
};

/**
 * ExpandablePostCard - Shows post content with expand/collapse functionality
 * Used in CreateContentPage (after generation) and PostsPage (saved posts)
 * Includes swipe-to-approve functionality for scheduled posts
 */
export default function ExpandablePostCard({ 
  post, 
  index = 0,
  onEdit,
  onDelete,
  onSchedule,
  onRegenerate,
  onUpdatePost,
  onApprove,
  onReject,
  showActions = true,
  defaultExpanded = false,
  isArchived = false
}) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showVoiceOptions, setShowVoiceOptions] = useState(false);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [editableEmail, setEditableEmail] = useState({
    subject: "",
    preview: "",
    greeting: "",
    body: ""
  });
  const [connectedEmail, setConnectedEmail] = useState(null);
  const [emailConnectionStatus, setEmailConnectionStatus] = useState(null);
  const [showAltEmailInput, setShowAltEmailInput] = useState(false);
  const [altEmail, setAltEmail] = useState("");
  const [checkingProvider, setCheckingProvider] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Ad state
  const [generatingAd, setGeneratingAd] = useState(false);
  const [showAdDialog, setShowAdDialog] = useState(false);
  const [generatedAd, setGeneratedAd] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [approving, setApproving] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(post.visual_filter || "none");
  const [applyingFilter, setApplyingFilter] = useState(false);
  const [showEditListingDetails, setShowEditListingDetails] = useState(false);
  const [editListingType, setEditListingType] = useState(post.listing_type || "property");
  const [editListingDetails, setEditListingDetails] = useState(post.listing_details || {});
  const [editTextPosition, setEditTextPosition] = useState(post.text_position || "bottom-center");
  const [savingListingDetails, setSavingListingDetails] = useState(false);
  const audioInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Check email connection status when dialog opens
  useEffect(() => {
    if (showEmailDialog) {
      checkEmailConnection();
    }
  }, [showEmailDialog]);
  
  const checkEmailConnection = async () => {
    try {
      const res = await apiClient.get("/oauth/email/status");
      setEmailConnectionStatus(res.data);
      if (res.data.primary_send_email) {
        setConnectedEmail(res.data.primary_send_email);
      } else if (res.data.auth_provider === "google") {
        // User signed up with Google but hasn't authorized email sending yet
        setConnectedEmail(null);
      }
    } catch (error) {
      console.error("Error checking email status:", error);
    }
  };
  
  // Handle Google OAuth callback via postMessage
  useEffect(() => {
    const handleOAuthMessage = (event) => {
      if (event.data?.type === 'google_oauth_success') {
        toast.success(`Email connected: ${event.data.email}`);
        setConnectedEmail(event.data.email);
        checkEmailConnection();
      } else if (event.data?.type === 'google_oauth_error') {
        toast.error(`Failed to connect email: ${event.data.error}`);
      }
    };
    
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, []);
  
  // Listing categories for editing
  const listingCategories = [
    { id: "property", label: "Property", icon: "🏠" },
    { id: "product", label: "Product", icon: "🛍️" },
    { id: "service", label: "Service", icon: "💼" },
    { id: "event", label: "Event", icon: "📅" },
    { id: "job", label: "Job", icon: "👔" },
    { id: "restaurant", label: "Restaurant", icon: "🍽️" },
    { id: "automotive", label: "Auto", icon: "🚗" },
    { id: "fitness", label: "Fitness", icon: "💪" },
    { id: "travel", label: "Travel", icon: "✈️" },
    { id: "education", label: "Education", icon: "📚" },
    { id: "pet", label: "Pet", icon: "🐾" },
    { id: "rental", label: "Rental", icon: "🔑" },
  ];
  
  // Text positions
  const textPositions = [
    { id: "top-left", label: "Top Left" },
    { id: "top-center", label: "Top Center" },
    { id: "top-right", label: "Top Right" },
    { id: "center-left", label: "Center Left" },
    { id: "center", label: "Center" },
    { id: "center-right", label: "Center Right" },
    { id: "bottom-left", label: "Bottom Left" },
    { id: "bottom-center", label: "Bottom Center" },
    { id: "bottom-right", label: "Bottom Right" },
  ];
  
  // Visual filters available
  const visualFilters = [
    { id: "none", name: "Original", icon: "○" },
    { id: "warm", name: "Warm", icon: "🌅" },
    { id: "cool", name: "Cool", icon: "❄️" },
    { id: "vintage", name: "Vintage", icon: "📷" },
    { id: "dramatic", name: "Dramatic", icon: "🎭" },
    { id: "bright", name: "Bright", icon: "☀️" },
    { id: "vivid", name: "Vivid", icon: "🎨" },
    { id: "bw", name: "B&W", icon: "⬛" },
  ];
  
  // Swipe gesture handling
  const x = useMotionValue(0);
  const rotateZ = useTransform(x, [-200, 0, 200], [-15, 0, 15]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0.5, 1, 1, 1, 0.5]);
  const rejectOpacity = useTransform(x, [-100, 0, 100], [1, 0, 0]);
  const approveOpacity = useTransform(x, [-100, 0, 100], [0, 0, 1]);
  
  const PlatformIcon = platformIcons[post.platform] || (() => <span>{post.platform?.[0]?.toUpperCase()}</span>);
  const platformColor = platformColors[post.platform] || "#FF6B35";
  
  // Handle filter application
  const handleApplyFilter = async (filterId) => {
    if (!post.image_url || isArchived) return;
    
    setApplyingFilter(true);
    try {
      // Use original_image_url for filter application (to avoid re-filtering)
      const sourceImage = post.original_image_url || post.image_url;
      
      // Send as URL-encoded form data
      const params = new URLSearchParams();
      params.append("image_url", sourceImage);
      params.append("filter_name", filterId);
      params.append("post_id", post.id);
      
      const token = localStorage.getItem("viraly_token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/apply-filter`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params.toString()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.image_url) {
        // Update the post with the new filtered image (keep original_image_url intact)
        const updateData = { 
          image_url: data.image_url,
          visual_filter: filterId
        };
        // Store original if not already stored
        if (!post.original_image_url) {
          updateData.original_image_url = post.image_url;
        }
        
        await apiClient.put(`/posts/${post.id}`, updateData);
        onUpdatePost?.({ 
          ...post, 
          image_url: data.image_url, 
          visual_filter: filterId,
          original_image_url: post.original_image_url || post.image_url
        });
        setSelectedFilter(filterId);
        toast.success(`${visualFilters.find(f => f.id === filterId)?.name || filterId} filter applied!`);
      }
    } catch (error) {
      console.error("Filter error details:", {
        message: error.message,
        name: error.name,
        stack: error.stack,
        error: error
      });
      toast.error(`Failed to apply filter: ${error.message}`);
    } finally {
      setApplyingFilter(false);
    }
  };
  
  // Handle saving listing details (Phase 4)
  const handleSaveListingDetails = async () => {
    setSavingListingDetails(true);
    try {
      await apiClient.put(`/posts/${post.id}`, {
        listing_type: editListingType,
        listing_details: editListingDetails,
        text_position: editTextPosition
      });
      
      onUpdatePost?.({
        ...post,
        listing_type: editListingType,
        listing_details: editListingDetails,
        text_position: editTextPosition
      });
      
      toast.success("Listing details saved!");
      setShowEditListingDetails(false);
    } catch (error) {
      console.error("Save listing details error:", error);
      toast.error("Failed to save listing details");
    } finally {
      setSavingListingDetails(false);
    }
  };
  
  // Handle approve action (with selected filter)
  const handleApprove = async () => {
    setApproving(true);
    try {
      // Apply filter first if changed from original
      if (selectedFilter !== "none" && selectedFilter !== post.visual_filter && post.image_url) {
        await handleApplyFilter(selectedFilter);
      }
      
      if (onApprove) {
        await onApprove(post.id);
      } else {
        // Default: mark as approved via API
        await apiClient.put(`/posts/${post.id}`, { status: "approved" });
        onUpdatePost?.({ ...post, status: "approved" });
      }
      toast.success("Post approved!");
      setExpanded(false); // Close expanded view after approval
    } catch (error) {
      toast.error("Failed to approve post");
    } finally {
      setApproving(false);
    }
  };
  
  // Handle reject action
  const handleReject = async () => {
    try {
      if (onReject) {
        await onReject(post.id);
      } else if (onDelete) {
        onDelete(post);
      } else {
        // Default: just close and show message
        toast.info("Post rejected");
      }
      setExpanded(false); // Close expanded view after rejection
    } catch (error) {
      toast.error("Failed to reject post");
    }
  };
  
  // Handle swipe end
  const handleDragEnd = (event, info) => {
    const threshold = 100;
    if (info.offset.x > threshold) {
      setSwipeDirection("right");
      handleApprove();
    } else if (info.offset.x < -threshold) {
      setSwipeDirection("left");
      handleReject();
    }
    x.set(0);
    setSwipeDirection(null);
  };
  
  const handleCopy = async () => {
    try {
      // Generate shareable link first
      const response = await apiClient.post(`/posts/${post.id}/share`);
      const shareUrl = response.data.share_url;
      
      // Build share text: Caption + Hashtags + Share Link
      const hashtags = post.hashtags?.map(t => t.startsWith('#') ? t : `#${t}`).join(' ') || '';
      const text = `${post.caption}\n\n${hashtags}\n\n🔗 ${shareUrl}`;
      
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Shareable link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback: just copy caption and hashtags without share link
      const hashtags = post.hashtags?.map(t => t.startsWith('#') ? t : `#${t}`).join(' ') || '';
      const text = `${post.caption}\n\n${hashtags}`;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Caption & hashtags copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleDownload = async () => {
    const mediaUrl = post.video_url || post.image_url;
    if (!mediaUrl) {
      toast.error("No media to download");
      return;
    }
    
    try {
      // Fetch the media file
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      
      // Generate filename
      const platform = post.platform || 'post';
      const baseFilename = `viraly_${platform}_${post.id?.slice(0, 8) || 'content'}`;
      
      // Get caption and hashtags
      const caption = post.caption || post.content || "";
      const hashtags = post.hashtags || [];
      const hashtagText = Array.isArray(hashtags) ? hashtags.join(' ') : hashtags;
      
      // For images, create a combined download (image + text in one HTML file that can be printed/saved)
      if (!post.video_url) {
        // Convert image to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Image = reader.result;
          
          // Create an HTML file with image and caption that can be saved/printed
          const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Viraly Post - ${platform}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 40px auto; padding: 20px; background: #1a1a2e; color: #fff; }
    .post-image { width: 100%; border-radius: 12px; margin-bottom: 20px; }
    .caption { font-size: 16px; line-height: 1.6; margin-bottom: 16px; white-space: pre-wrap; }
    .hashtags { color: #ff6b35; font-size: 14px; margin-bottom: 20px; }
    .meta { color: #888; font-size: 12px; border-top: 1px solid #333; padding-top: 16px; margin-top: 20px; }
    .copy-btn { background: #ff6b35; color: white; border: none; padding: 10px 20px; border-radius: 8px; cursor: pointer; margin-right: 10px; }
    .copy-btn:hover { background: #ff8555; }
    @media print { body { background: white; color: black; } .hashtags { color: #ff6b35; } .copy-btn { display: none; } }
  </style>
</head>
<body>
  <img src="${base64Image}" class="post-image" alt="Post Image">
  <div class="caption">${caption.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
  <div class="hashtags">${hashtagText}</div>
  <div style="margin: 20px 0;">
    <button class="copy-btn" onclick="navigator.clipboard.writeText(document.querySelector('.caption').innerText + '\\n\\n' + document.querySelector('.hashtags').innerText); this.innerText='Copied!';">Copy Caption & Hashtags</button>
  </div>
  <div class="meta">
    <p>Platform: ${platform} | Created: ${post.created_at ? new Date(post.created_at).toLocaleString() : 'N/A'}</p>
    <p>Generated by Viraly (viraly.ca)</p>
  </div>
</body>
</html>`;
          
          const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
          const htmlUrl = window.URL.createObjectURL(htmlBlob);
          const a = document.createElement('a');
          a.href = htmlUrl;
          a.download = `${baseFilename}_complete.html`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(htmlUrl);
          
          toast.success("Downloaded! Open the HTML file to view & copy caption");
        };
        reader.readAsDataURL(blob);
      } else {
        // For videos, download video + separate text file
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseFilename}.mp4`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        // Also download caption text file for videos
        const textContent = `CAPTION:\n${caption}\n\nHASHTAGS:\n${hashtagText}\n\nPLATFORM: ${platform}\nCREATED: ${post.created_at ? new Date(post.created_at).toLocaleString() : 'N/A'}\n\n---\nGenerated by Viraly (viraly.ca)`;
        const textBlob = new Blob([textContent], { type: 'text/plain' });
        const textUrl = window.URL.createObjectURL(textBlob);
        const textLink = document.createElement('a');
        textLink.href = textUrl;
        textLink.download = `${baseFilename}_caption.txt`;
        document.body.appendChild(textLink);
        textLink.click();
        document.body.removeChild(textLink);
        window.URL.revokeObjectURL(textUrl);
        
        toast.success("Downloaded video + caption!");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Download failed. Try right-clicking and 'Save As'");
    }
  };
  
  const handleRegenerate = async () => {
    if (onRegenerate) {
      // Show confirmation with credit cost
      const creditType = post.type === 'video' ? 'video credit' : 'post credit';
      const confirmed = window.confirm(`This will use 1 ${creditType}. Continue?`);
      if (!confirmed) return;
      
      setRegenerating(true);
      try {
        await onRegenerate(post.id);
      } finally {
        setRegenerating(false);
      }
    }
  };
  
  // Voice upload handler
  const handleVoiceUpload = async (file) => {
    if (!file) return;
    
    setUploadingVoice(true);
    try {
      const formData = new FormData();
      formData.append("audio", file);
      formData.append("post_id", post.id);
      
      const res = await apiClient.post("/posts/custom-voice", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (res.data.success) {
        toast.success("Voice uploaded! Your video will be updated.");
        if (onUpdatePost) {
          onUpdatePost(res.data.post);
        }
      }
    } catch (error) {
      toast.error("Failed to upload voice. Try again.");
    } finally {
      setUploadingVoice(false);
      setShowVoiceOptions(false);
    }
  };
  
  // Voice recording handlers
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mp3' });
        const audioFile = new File([audioBlob], "recording.mp3", { type: 'audio/mp3' });
        await handleVoiceUpload(audioFile);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setRecording(true);
      toast.info("Recording... Click stop when done.");
    } catch (error) {
      toast.error("Microphone access denied");
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };
  
  const truncatedCaption = post.caption?.length > 120 
    ? post.caption.substring(0, 120) + "..." 
    : post.caption;

  // Disable right-click for archived posts
  const handleContextMenu = (e) => {
    if (isArchived) {
      e.preventDefault();
      return false;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`card-base overflow-hidden ${isArchived ? 'opacity-50 grayscale pointer-events-auto select-none' : ''}`}
      data-testid={`post-card-${index}`}
      onContextMenu={handleContextMenu}
      style={isArchived ? { userSelect: 'none', WebkitUserSelect: 'none' } : {}}
    >
      {/* Archived Overlay Notice */}
      {isArchived && (
        <div className="bg-gray-800/80 text-gray-400 text-xs px-4 py-2 flex items-center gap-2">
          <Archive className="w-3 h-3" />
          <span>This post is archived and view-only. Will be deleted in {
            post.archived_at 
              ? Math.max(0, 30 - Math.floor((Date.now() - new Date(post.archived_at).getTime()) / (1000 * 60 * 60 * 24)))
              : 30
          } days.</span>
        </div>
      )}
      
      {/* Header - Always Visible */}
      <div 
        className={`p-4 cursor-pointer hover:bg-white/5 transition-colors ${isArchived ? 'cursor-default hover:bg-transparent' : ''}`}
        onClick={() => !isArchived && setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          {/* Thumbnail Preview - Shows image/video thumbnail in collapsed view */}
          {!expanded && (post.thumbnail_url || post.image_url || post.video_url) && (
            <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-black/30">
              {post.thumbnail_url ? (
                <img 
                  src={post.thumbnail_url} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : post.image_url ? (
                <img 
                  src={post.image_url} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : post.video_url ? (
                <video 
                  src={post.video_url} 
                  className="w-full h-full object-cover"
                  muted
                  preload="metadata"
                />
              ) : null}
              {/* Video play indicator */}
              {(post.type === "video" || post.video_url) && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center">
                    <div className="w-0 h-0 border-l-[6px] border-l-black border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5" />
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Platform Icon - Only show when no thumbnail */}
          {(expanded || (!post.thumbnail_url && !post.image_url && !post.video_url)) && (
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: platformColor + "20" }}
            >
              <PlatformIcon className="w-5 h-5" style={{ color: platformColor }} />
            </div>
          )}
          
          {/* Content Preview */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold capitalize">{post.platform}</span>
                {/* Content type indicator */}
                {(() => {
                  const contentType = getContentTypeIcon(post);
                  const ContentIcon = contentType.icon;
                  return (
                    <span className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-white/10 ${contentType.color}`}>
                      <ContentIcon className="w-3 h-3" />
                      {contentType.label}
                    </span>
                  );
                })()}
                {/* Scheduled date at top */}
                {post.scheduled_at && (
                  <span className="flex items-center gap-1 text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">
                    <Calendar className="w-3 h-3" />
                    {new Date(post.scheduled_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </span>
                )}
                {post.viral_score && (
                  <span className="flex items-center gap-1 text-viraly-orange text-sm">
                    <Zap className="w-3 h-3" />
                    {post.viral_score}/100
                  </span>
                )}
                {/* Posted date with time for published posts */}
                {post.published_at && post.status === 'published' && (
                  <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
                    <Check className="w-3 h-3" />
                    Posted {new Date(post.published_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </span>
                )}
                {/* Archived date for archived posts - greyed out */}
                {post.archived_at && isArchived && (
                  <span className="flex items-center gap-1 text-xs text-gray-400 bg-gray-500/10 px-2 py-0.5 rounded-full">
                    <Archive className="w-3 h-3" />
                    Archived {new Date(post.archived_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </span>
                )}
                {post.status && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    post.status === 'published' ? 'bg-viraly-success/20 text-viraly-success' :
                    post.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                    post.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                    post.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                    'bg-white/10 text-viraly-muted'
                  }`}>
                    {post.status}
                  </span>
                )}
                {/* NEW badge - only for scheduled/pending posts needing approval */}
                {post.created_at && 
                 (new Date() - new Date(post.created_at)) < 24 * 60 * 60 * 1000 && 
                 ['scheduled', 'pending', 'draft'].includes(post.status) && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 text-white animate-pulse">
                    NEW
                  </span>
                )}
                {/* AI Voice badge for videos with audio */}
                {(post.type === "video" || post.video_url) && post.has_audio && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 flex items-center gap-1">
                    <Volume2 className="w-3 h-3" />
                    AI Voice
                  </span>
                )}
                {/* Regenerated badge */}
                {post.regenerated_from && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" />
                    Regen
                  </span>
                )}
              </div>
              
              <button className="p-1 hover:bg-white/10 rounded transition-colors">
                {expanded ? (
                  <ChevronUp className="w-5 h-5 text-viraly-muted" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-viraly-muted" />
                )}
              </button>
            </div>
            
            {/* Title if exists */}
            {post.title && !expanded && (
              <p className="text-viraly-orange text-sm font-medium mb-1">{post.title}</p>
            )}
            
            {/* Truncated Caption */}
            {!expanded && (
              <p className="text-viraly-light text-sm line-clamp-2">{truncatedCaption}</p>
            )}
            
            {/* Quick action row for scheduled posts - collapsed view */}
            {!expanded && post.status === "scheduled" && (
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(true);
                  }}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-viraly-orange/20 text-viraly-orange hover:bg-viraly-orange hover:text-white transition-all font-medium"
                  data-testid="quick-swipe-approve"
                >
                  <Check className="w-3 h-3" />
                  <span className="hidden sm:inline">Review & Approve</span>
                  <span className="sm:hidden">Tap to Approve</span>
                  <ChevronDown className="w-3 h-3 ml-1" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-4">
              {/* Archived Post Notice */}
              {isArchived && (
                <div className="p-3 rounded-xl bg-gray-500/10 border border-gray-500/30 text-center">
                  <p className="text-sm text-gray-400">
                    📦 This post is archived and view-only. It was regenerated and will be auto-deleted after 30 days.
                  </p>
                  <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-500">
                    {post.archived_at && (
                      <span>
                        Archived: {new Date(post.archived_at).toLocaleDateString()} at {new Date(post.archived_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    )}
                    {post.expires_at && (
                      <span>
                        Auto-deletes: {new Date(post.expires_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-600 mt-2">
                    Actions like edit, regenerate, schedule, and email are disabled for archived posts.
                  </p>
                </div>
              )}
              
              {/* Approve/Reject Section - At TOP for scheduled/pending posts */}
              {(post.status === "scheduled" || post.status === "pending") && !isArchived && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-red-500/10 via-transparent to-green-500/10 border border-white/10">
                  {/* Filter Selection - Only show if post has an image */}
                  {post.image_url && !post.video_url && (
                    <div className="mb-4">
                      <p className="text-xs text-viraly-muted mb-2 text-center">Apply Visual Filter</p>
                      <div className="flex flex-wrap justify-center gap-1.5">
                        {visualFilters.map((filter) => (
                          <button
                            key={filter.id}
                            onClick={() => handleApplyFilter(filter.id)}
                            disabled={applyingFilter}
                            className={`px-2 py-1 rounded-lg text-xs transition-all ${
                              selectedFilter === filter.id
                                ? "bg-viraly-orange/30 border-viraly-orange text-white border"
                                : "bg-white/5 border-white/10 text-viraly-muted border hover:bg-white/10"
                            }`}
                            data-testid={`filter-${filter.id}`}
                          >
                            {applyingFilter ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1 inline" />
                            ) : (
                              <span className="mr-1">{filter.icon}</span>
                            )}
                            {filter.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      onClick={handleReject}
                      variant="outline"
                      className="flex-1 max-w-[150px] bg-red-500/10 hover:bg-red-500/30 text-red-400 border-red-500/30 hover:border-red-500"
                      data-testid="reject-post-btn"
                    >
                      <ThumbsDown className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      onClick={handleApprove}
                      disabled={approving || applyingFilter}
                      className="flex-1 max-w-[150px] bg-green-500/20 hover:bg-green-500/40 text-green-400 border border-green-500/30"
                      data-testid="approve-post-btn"
                    >
                      {(approving || applyingFilter) ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ThumbsUp className="w-4 h-4 mr-2" />
                      )}
                      {applyingFilter ? "Applying..." : "Approve"}
                    </Button>
                  </div>
                  <p className="text-center text-[10px] text-viraly-muted mt-2 sm:hidden">
                    Swipe left to reject, right to approve
                  </p>
                </div>
              )}
              
              {/* Edit Listing Details Section (Phase 4) - For scheduled/pending posts with images */}
              {(post.status === "scheduled" || post.status === "pending") && !isArchived && post.image_url && !post.video_url && (
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-viraly-orange" />
                      <span className="text-sm font-medium">Edit Listing Details</span>
                    </div>
                    <button
                      onClick={() => setShowEditListingDetails(!showEditListingDetails)}
                      className="text-xs text-viraly-orange hover:underline"
                      data-testid="toggle-edit-listing"
                    >
                      {showEditListingDetails ? "Hide" : "Show"}
                    </button>
                  </div>
                  
                  {showEditListingDetails && (
                    <div className="space-y-3">
                      {/* Category Selector */}
                      <div className="flex gap-1.5 flex-wrap">
                        {listingCategories.map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => setEditListingType(cat.id)}
                            className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                              editListingType === cat.id
                                ? "bg-viraly-orange text-white"
                                : "bg-white/10 text-viraly-muted hover:bg-white/20"
                            }`}
                          >
                            <span>{cat.icon}</span>
                            <span>{cat.label}</span>
                          </button>
                        ))}
                      </div>
                      
                      {/* Quick Fields Based on Category */}
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          placeholder={editListingType === "property" ? "Price (e.g., $899,000)" : "Price"}
                          value={editListingDetails.price || ""}
                          onChange={(e) => setEditListingDetails({...editListingDetails, price: e.target.value})}
                          className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm"
                        />
                        <input
                          placeholder={editListingType === "property" ? "Address" : "Name/Title"}
                          value={editListingDetails.address || editListingDetails.name || ""}
                          onChange={(e) => setEditListingDetails({...editListingDetails, [editListingType === "property" ? "address" : "name"]: e.target.value})}
                          className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm"
                        />
                      </div>
                      
                      {/* Text Position */}
                      <div>
                        <label className="text-xs text-viraly-muted mb-1.5 block">Text Position</label>
                        <div className="grid grid-cols-3 gap-1">
                          {textPositions.map((pos) => (
                            <button
                              key={pos.id}
                              onClick={() => setEditTextPosition(pos.id)}
                              className={`px-2 py-1 rounded text-[10px] ${
                                editTextPosition === pos.id
                                  ? "bg-viraly-orange text-white"
                                  : "bg-white/10 text-viraly-muted hover:bg-white/20"
                              }`}
                            >
                              {pos.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Save Button */}
                      <Button
                        onClick={handleSaveListingDetails}
                        disabled={savingListingDetails}
                        className="w-full bg-viraly-orange hover:bg-viraly-orange/80"
                        size="sm"
                        data-testid="save-listing-details"
                      >
                        {savingListingDetails ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-2" />
                        )}
                        Save & Regenerate Image
                      </Button>
                      <p className="text-[10px] text-viraly-muted text-center">
                        Note: Saving will regenerate the image with new overlay text
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Full Caption */}
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-viraly-light whitespace-pre-wrap">{post.caption}</p>
              </div>
              
              {/* Video Preview - Show video player with thumbnail poster */}
              {post.video_url && (
                <div className="rounded-xl overflow-hidden bg-black">
                  <video 
                    src={post.video_url}
                    controls
                    className="w-full"
                    poster={post.thumbnail_url}
                  />
                </div>
              )}
              
              {/* Full Image Preview - Show if image_url exists and no video */}
              {post.image_url && !post.video_url && (
                <div className="relative rounded-xl overflow-hidden bg-black/20">
                  <img 
                    src={post.image_url} 
                    alt="Post content" 
                    className="w-full object-contain max-h-[500px]"
                  />
                  {/* Viraly Watermark - Positioned on image */}
                  <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm">
                    <div className="w-4 h-4 rounded bg-viraly-gradient flex items-center justify-center">
                      <span className="text-[8px] font-bold text-white">V</span>
                    </div>
                    <span className="text-[10px] font-medium text-white/90">Viraly</span>
                  </div>
                </div>
              )}
              
              {/* Filter Selection for Approved posts - Allow editing filter before posting */}
              {post.status === "approved" && !isArchived && post.image_url && !post.video_url && (
                <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-xs text-viraly-muted mb-2 text-center">Change Visual Filter</p>
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {visualFilters.map((filter) => (
                      <button
                        key={filter.id}
                        onClick={() => handleApplyFilter(filter.id)}
                        disabled={applyingFilter}
                        className={`px-2 py-1 rounded-lg text-xs transition-all ${
                          selectedFilter === filter.id
                            ? "bg-viraly-orange/30 border-viraly-orange text-white border"
                            : "bg-white/5 border-white/10 text-viraly-muted border hover:bg-white/10"
                        }`}
                        data-testid={`filter-approved-${filter.id}`}
                      >
                        {applyingFilter ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1 inline" />
                        ) : (
                          <span className="mr-1">{filter.icon}</span>
                        )}
                        {filter.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Thumbnail Preview - Fallback when no video player but thumbnail exists */}
              {post.thumbnail_url && !post.video_url && !post.image_url && (
                <div className="relative rounded-xl overflow-hidden bg-black/20">
                  <img 
                    src={post.thumbnail_url} 
                    alt="Content preview" 
                    className="w-full object-contain max-h-[500px]"
                  />
                  <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm">
                    <div className="w-4 h-4 rounded bg-viraly-gradient flex items-center justify-center">
                      <span className="text-[8px] font-bold text-white">V</span>
                    </div>
                    <span className="text-[10px] font-medium text-white/90">Viraly</span>
                  </div>
                </div>
              )}
              
              {/* Branding Style Switch - Show for posts with branding */}
              {(post.branding_style || post.post_style === "my_brand") && post.image_url && (
                <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-viraly-muted">Branding:</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        post.branding_style === "prominent" 
                          ? "bg-purple-500/20 text-purple-400" 
                          : "bg-blue-500/20 text-blue-400"
                      }`}>
                        {post.branding_style === "prominent" ? "Prominent" : "Subtle"}
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        const newStyle = post.branding_style === "prominent" ? "subtle" : "prominent";
                        try {
                          const token = localStorage.getItem("viraly_token");
                          const response = await fetch(
                            `${process.env.REACT_APP_BACKEND_URL}/api/posts/${post.id}/switch-branding?branding_style=${newStyle}`,
                            {
                              method: "POST",
                              headers: { Authorization: `Bearer ${token}` }
                            }
                          );
                          if (response.ok) {
                            const data = await response.json();
                            // Update local state - parent should refresh
                            window.location.reload();
                          }
                        } catch (err) {
                          console.error("Failed to switch branding:", err);
                        }
                      }}
                      className="text-xs px-3 py-1.5 rounded-lg bg-viraly-orange/20 hover:bg-viraly-orange/30 text-viraly-orange transition-colors"
                      data-testid="switch-branding-btn"
                    >
                      Switch to {post.branding_style === "prominent" ? "Subtle" : "Prominent"}
                      <span className="ml-1 text-[10px] opacity-70">FREE</span>
                    </button>
                  </div>
                </div>
              )}
              
              {/* Custom Voice Options - Only for video posts */}
              {(post.type === "video" || post.video_url) && (
                <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                  {/* Collapsed view - just show voice status and action */}
                  {!showVoiceOptions ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {post.has_custom_voice ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400 flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Your Voice
                          </span>
                        ) : post.has_audio ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 flex items-center gap-1">
                            <Volume2 className="w-3 h-3" />
                            Viraly AI Voice
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 flex items-center gap-1">
                            <Volume2 className="w-3 h-3" />
                            Viraly AI Voice
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setShowVoiceOptions(true)}
                        className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-viraly-light transition-colors"
                        data-testid="add-voice-btn"
                      >
                        {post.has_custom_voice || post.has_audio ? "Replace" : "Add Voice"}
                      </button>
                    </div>
                  ) : (
                    /* Expanded voice options */
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Replace Voice</span>
                        <button
                          onClick={() => setShowVoiceOptions(false)}
                          className="text-viraly-muted hover:text-white text-sm"
                        >
                          ✕
                        </button>
                      </div>
                      
                      <div className="p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                        <p className="text-xs text-yellow-400 font-medium">
                          ⚠️ This will REPLACE the AI voiceover completely
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={recording ? stopRecording : startRecording}
                          className={`flex-1 ${recording ? 'bg-red-500 hover:bg-red-600' : 'btn-secondary'}`}
                          disabled={uploadingVoice}
                          data-testid="record-voice-btn"
                        >
                          <Mic className={`w-4 h-4 mr-2 ${recording ? 'animate-pulse' : ''}`} />
                          {recording ? "Stop" : "Record"}
                        </Button>
                        
                        <Button
                          onClick={() => audioInputRef.current?.click()}
                          className="flex-1 btn-secondary"
                          disabled={uploadingVoice || recording}
                          data-testid="upload-voice-btn"
                        >
                          {uploadingVoice ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload
                        </Button>
                        
                        <input
                          ref={audioInputRef}
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && handleVoiceUpload(e.target.files[0])}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Hashtags */}
              {post.hashtags && post.hashtags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {post.hashtags.map((tag, j) => (
                    <span 
                      key={j} 
                      className="px-2 py-1 rounded-full bg-viraly-orange/20 text-viraly-orange text-sm"
                    >
                      {tag.startsWith("#") ? tag : `#${tag}`}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Timestamps Section */}
              <div className="space-y-1">
                {/* Created Time */}
                {post.created_at && (
                  <div className="flex items-center gap-2 text-sm text-viraly-muted">
                    <FileText className="w-4 h-4 text-viraly-muted" />
                    <span>Created: {new Date(post.created_at).toLocaleString()}</span>
                  </div>
                )}
                
                {/* Approved Time - only show if actually approved or published */}
                {post.approved_at && (post.status === "approved" || post.status === "published") && (
                  <div className="flex items-center gap-2 text-sm text-blue-400">
                    <CheckCircle className="w-4 h-4" />
                    <span>Approved: {new Date(post.approved_at).toLocaleString()}</span>
                  </div>
                )}
                
                {/* Regenerated Time */}
                {post.regenerated_at && (
                  <div className="flex items-center gap-2 text-sm text-amber-400/80">
                    <RefreshCw className="w-4 h-4" />
                    <span>Regenerated: {new Date(post.regenerated_at).toLocaleString()}</span>
                  </div>
                )}
                
                {/* Scheduled Time */}
                {post.scheduled_at && post.status !== "published" && (
                  <div className="flex items-center gap-2 text-sm text-viraly-orange">
                    <Calendar className="w-4 h-4" />
                    <span>Scheduled for: {new Date(post.scheduled_at).toLocaleString()}</span>
                  </div>
                )}
                
                {/* Published/Posted Time - only show for actually published posts */}
                {post.published_at && post.status === "published" && (
                  <div className="flex items-center gap-2 text-sm text-green-400">
                    <Check className="w-4 h-4" />
                    <span>Posted: {new Date(post.published_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
              
              {/* Viral Score Details */}
              {post.viral_score && (
                <div className="p-3 rounded-xl bg-viraly-orange/10 border border-viraly-orange/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Zap className="w-4 h-4 text-viraly-orange" />
                      Viral Score
                    </span>
                    <span className="text-viraly-orange font-bold">{post.viral_score}/100</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-viraly-gradient transition-all"
                      style={{ width: `${post.viral_score}%` }}
                    />
                  </div>
                  {/* Score improvement tips based on current score */}
                  {post.viral_score < 50 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-red-400 font-medium">Score needs improvement:</p>
                      <ul className="text-xs text-viraly-muted list-disc list-inside">
                        <li>Add 5-10 trending hashtags for your niche</li>
                        <li>Use "Personal Brand" style for better engagement</li>
                        <li>Include a clear call-to-action in caption</li>
                      </ul>
                    </div>
                  )}
                  {post.viral_score >= 50 && post.viral_score < 70 && (
                    <p className="text-xs text-yellow-400 mt-2">
                      Good start! Add more hashtags or try Regenerate for a higher score.
                    </p>
                  )}
                  {post.viral_score >= 70 && post.viral_score < 85 && (
                    <p className="text-xs text-green-400 mt-2">
                      Great score! Post at peak hours (9AM, 12PM, 6PM) for best reach.
                    </p>
                  )}
                  {post.viral_score >= 85 && (
                    <p className="text-xs text-viraly-orange mt-2 font-medium">
                      🔥 Excellent! This post has high viral potential!
                    </p>
                  )}
                </div>
              )}
              
              {/* Generate Email Promotion - FREE Feature - DISABLED for archived posts */}
              {!isArchived && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-viraly-orange/10 to-purple-500/10 border border-viraly-orange/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-viraly-orange/20 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-viraly-orange" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Email this content</p>
                        <p className="text-xs text-viraly-muted">FREE - Send to your subscribers</p>
                      </div>
                    </div>
                    <Button
                      onClick={async () => {
                        setGeneratingEmail(true);
                        try {
                          const res = await apiClient.post("/content/generate-email", {
                            text: post.caption,
                            post_id: post.id,
                            email_type: post.type === "video" ? "promo" : "newsletter"
                          });
                          setGeneratedEmail(res.data.email_content);
                          setEditableEmail({
                            subject: res.data.email_content.subject_line || "",
                            preview: res.data.email_content.preview_text || "",
                            greeting: res.data.email_content.greeting || "",
                            body: res.data.email_content.body || ""
                          });
                          setEmailRecipient("");
                          setShowEmailDialog(true);
                          setShowAltEmailInput(false);
                          setAltEmail("");
                        } catch (error) {
                          console.error("Email generation error:", error);
                          toast.error("Failed to generate email");
                        } finally {
                          setGeneratingEmail(false);
                        }
                      }}
                      className="btn-primary px-4 py-2 text-sm font-medium"
                      disabled={generatingEmail}
                      data-testid="generate-email-btn"
                    >
                      {generatingEmail ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Generate Email
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Email Dialog with Gmail OAuth */}
                  {showEmailDialog && generatedEmail && (
                  <div className="mt-4 p-4 rounded-lg bg-viraly-dark border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">Email Preview</span>
                      <button 
                        onClick={() => {
                          setShowEmailDialog(false);
                          setShowAltEmailInput(false);
                        }}
                        className="text-viraly-muted hover:text-white"
                      >
                        ✕
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      {/* Editable Email Fields */}
                      <div>
                        <label className="text-xs text-viraly-muted block mb-1">Subject Line</label>
                        <input
                          type="text"
                          value={editableEmail.subject}
                          onChange={(e) => setEditableEmail(prev => ({...prev, subject: e.target.value}))}
                          className="input-base w-full text-sm"
                          placeholder="Email subject..."
                        />
                      </div>
                      
                      <div>
                        <label className="text-xs text-viraly-muted block mb-1">Preview Text</label>
                        <input
                          type="text"
                          value={editableEmail.preview}
                          onChange={(e) => setEditableEmail(prev => ({...prev, preview: e.target.value}))}
                          className="input-base w-full text-xs"
                          placeholder="Preview text shown in inbox..."
                        />
                      </div>
                      
                      <div>
                        <label className="text-xs text-viraly-muted block mb-1">Greeting</label>
                        <input
                          type="text"
                          value={editableEmail.greeting}
                          onChange={(e) => setEditableEmail(prev => ({...prev, greeting: e.target.value}))}
                          className="input-base w-full text-sm"
                          placeholder="Hi there!"
                        />
                      </div>
                      
                      <div>
                        <label className="text-xs text-viraly-muted block mb-1">Body</label>
                        <textarea
                          value={editableEmail.body}
                          onChange={(e) => setEditableEmail(prev => ({...prev, body: e.target.value}))}
                          className="input-base w-full text-sm min-h-[120px] resize-y"
                          placeholder="Email body content..."
                        />
                      </div>
                      
                      <div>
                        <label className="text-xs text-viraly-muted mb-1 block">Send to</label>
                        <input
                          type="email"
                          value={emailRecipient}
                          onChange={(e) => setEmailRecipient(e.target.value)}
                          className="input-base w-full text-sm"
                          placeholder="recipient@email.com"
                        />
                      </div>
                      
                      {/* Send From Section - Gmail OAuth */}
                      <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                        {connectedEmail ? (
                          <>
                            <p className="text-xs text-viraly-muted mb-2">
                              This email will be sent from:
                            </p>
                            <p className="text-sm font-medium text-viraly-orange mb-2">
                              {connectedEmail}
                            </p>
                            
                            {!showAltEmailInput ? (
                              <button
                                onClick={() => setShowAltEmailInput(true)}
                                className="text-xs text-viraly-muted hover:text-white underline"
                              >
                                Use a different email?
                              </button>
                            ) : (
                              <div className="mt-2 space-y-2">
                                <input
                                  type="email"
                                  value={altEmail}
                                  onChange={(e) => setAltEmail(e.target.value)}
                                  className="input-base w-full text-sm"
                                  placeholder="yourname@company.com"
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={async () => {
                                      if (!altEmail.includes("@")) {
                                        toast.error("Please enter a valid email");
                                        return;
                                      }
                                      setCheckingProvider(true);
                                      try {
                                        const res = await apiClient.post(`/oauth/email/detect-provider?email=${encodeURIComponent(altEmail)}`);
                                        if (res.data.provider === "google" && res.data.supported) {
                                          // Open Google OAuth in popup
                                          const authRes = await apiClient.get("/oauth/google/connect");
                                          const popup = window.open(authRes.data.auth_url, "Google OAuth", "width=500,height=600");
                                          if (!popup) {
                                            toast.error("Please allow popups for this site");
                                          }
                                        } else if (res.data.provider === "microsoft") {
                                          toast.info("Microsoft 365 support coming soon!");
                                        } else {
                                          toast.error(res.data.message || "This email provider is not supported yet");
                                        }
                                      } catch (error) {
                                        toast.error("Could not detect email provider");
                                      } finally {
                                        setCheckingProvider(false);
                                      }
                                    }}
                                    disabled={checkingProvider}
                                    className="flex-1"
                                  >
                                    {checkingProvider ? (
                                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                    ) : null}
                                    Connect
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setShowAltEmailInput(false);
                                      setAltEmail("");
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <p className="text-xs text-viraly-muted mb-2">
                              Connect your Gmail to send emails:
                            </p>
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  const res = await apiClient.get("/oauth/google/connect");
                                  const popup = window.open(res.data.auth_url, "Google OAuth", "width=500,height=600");
                                  if (!popup) {
                                    toast.error("Please allow popups for this site");
                                  }
                                } catch (error) {
                                  toast.error("Failed to start Google authentication");
                                }
                              }}
                              className="w-full bg-white text-gray-800 hover:bg-gray-100"
                            >
                              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                              </svg>
                              Connect Gmail
                            </Button>
                            <p className="text-[10px] text-viraly-muted mt-2 text-center">
                              Only Gmail & Google Workspace supported. Microsoft 365 coming soon.
                            </p>
                          </>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={async () => {
                            if (!connectedEmail) {
                              toast.error("Please connect your Gmail first");
                              return;
                            }
                            if (!emailRecipient) {
                              toast.error("Please enter a recipient email");
                              return;
                            }
                            
                            setSendingEmail(true);
                            try {
                              const fullBody = `${editableEmail.greeting}\n\n${editableEmail.body}\n\n${generatedEmail.cta_text}: ${generatedEmail.cta_url_placeholder}\n\n${generatedEmail.signature}`;
                              
                              await apiClient.post("/oauth/email/send-gmail", null, {
                                params: {
                                  to_email: emailRecipient,
                                  subject: editableEmail.subject,
                                  body: fullBody,
                                  post_id: post.id
                                }
                              });
                              
                              toast.success(`Email sent from ${connectedEmail}!`);
                              setShowEmailDialog(false);
                            } catch (error) {
                              console.error("Send email error:", error);
                              toast.error(error.response?.data?.detail || "Failed to send email");
                            } finally {
                              setSendingEmail(false);
                            }
                          }}
                          className="btn-primary flex-1"
                          disabled={!connectedEmail || !emailRecipient || sendingEmail}
                          data-testid="send-email-btn"
                        >
                          {sendingEmail ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Email
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => {
                            const fullEmail = `Subject: ${editableEmail.subject}\n\n${editableEmail.greeting}\n\n${editableEmail.body}\n\n${generatedEmail.cta_text}: ${generatedEmail.cta_url_placeholder}\n\n${generatedEmail.signature}`;
                            navigator.clipboard.writeText(fullEmail);
                            toast.success("Email copied to clipboard!");
                          }}
                          className="btn-secondary"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}
              
              {/* Create Ad - FREE Feature - DISABLED for archived posts */}
              {!isArchived && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Create Ad</p>
                        <p className="text-xs text-viraly-muted">FREE - Generate ad copy for paid campaigns</p>
                      </div>
                    </div>
                    <Button
                      onClick={async () => {
                        setGeneratingAd(true);
                        try {
                          const res = await apiClient.post("/content/generate-ad", {
                            text: post.caption,
                            post_id: post.id,
                            platforms: ["facebook", "instagram", "tiktok", "pinterest", "linkedin", "youtube", "twitter"]
                          });
                          setGeneratedAd(res.data.ad_content);
                          setShowAdDialog(true);
                        } catch (error) {
                          console.error("Ad generation error:", error);
                          toast.error("Failed to generate ad");
                        } finally {
                          setGeneratingAd(false);
                        }
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 text-sm font-medium"
                      disabled={generatingAd}
                      data-testid="generate-ad-btn"
                    >
                      {generatingAd ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Generate Ad
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Ad Preview Dialog */}
                  {showAdDialog && generatedAd && (
                    <div className="mt-4 p-4 rounded-lg bg-viraly-dark border border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium">Ad Copy Preview</span>
                        <button 
                          onClick={() => setShowAdDialog(false)}
                          className="text-viraly-muted hover:text-white"
                        >
                          ✕
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-viraly-muted">Primary Text (Headline)</label>
                          <p className="text-sm p-2 bg-white/5 rounded mt-1">{generatedAd.headline}</p>
                        </div>
                        <div>
                          <label className="text-xs text-viraly-muted">Description</label>
                          <p className="text-sm p-2 bg-white/5 rounded mt-1">{generatedAd.description}</p>
                        </div>
                        <div>
                          <label className="text-xs text-viraly-muted">Call to Action</label>
                          <p className="text-sm p-2 bg-white/5 rounded mt-1">{generatedAd.cta}</p>
                        </div>
                        
                        <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                          <p className="text-xs text-viraly-muted flex-1">
                            Copy and paste into Facebook/Instagram Ads Manager, TikTok Ads, or Google Ads
                          </p>
                          <Button
                            onClick={() => {
                              const adText = `=== AD COPY ===

HEADLINE:
${generatedAd.headline}

DESCRIPTION:
${generatedAd.description}

CALL TO ACTION:
${generatedAd.cta}

=== PLATFORM-SPECIFIC ===

FACEBOOK PRIMARY TEXT:
${generatedAd.facebook_primary_text || generatedAd.headline}

INSTAGRAM CAPTION:
${generatedAd.instagram_caption || generatedAd.description}

TIKTOK TEXT:
${generatedAd.tiktok_text || generatedAd.headline}

${generatedAd.variations?.length ? `\n=== ALTERNATIVE HEADLINES ===\n${generatedAd.variations.join('\n')}` : ''}`;
                              navigator.clipboard.writeText(adText);
                              toast.success("Complete ad copy copied to clipboard!");
                            }}
                            className="btn-primary"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy All
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Actions - disabled for archived posts */}
              {showActions && !isArchived && (
                <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                  <Button
                    onClick={handleCopy}
                    variant="ghost"
                    size="sm"
                    className="text-viraly-muted hover:text-white"
                  >
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  
                  {/* Download button - shows for posts with media */}
                  {(post.image_url || post.video_url) && (
                    <Button
                      onClick={handleDownload}
                      variant="ghost"
                      size="sm"
                      className="text-green-400 hover:text-green-300"
                      data-testid="download-btn"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  )}
                  
                  {onRegenerate && !post.regenerated_from && (
                    <Button
                      onClick={handleRegenerate}
                      variant="ghost"
                      size="sm"
                      className="text-viraly-orange hover:text-orange-300"
                      disabled={regenerating}
                      data-testid="regenerate-btn"
                    >
                      {regenerating ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-1" />
                      )}
                      Regenerate
                    </Button>
                  )}
                  
                  {/* Show regenerated badge if this post was regenerated */}
                  {post.regenerated_from && (
                    <span className="text-xs text-amber-400/70 bg-amber-400/10 px-2 py-1 rounded">
                      Regenerated
                    </span>
                  )}
                  
                  {onEdit && (
                    <Button
                      onClick={() => onEdit(post)}
                      variant="ghost"
                      size="sm"
                      className="text-viraly-muted hover:text-white"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  
                  {onSchedule && post.status === 'draft' && (
                    <Button
                      onClick={() => onSchedule(post)}
                      variant="ghost"
                      size="sm"
                      className="text-viraly-muted hover:text-white"
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Schedule
                    </Button>
                  )}
                  
                  {onDelete && (
                    <Button
                      onClick={() => onDelete(post)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 ml-auto"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
